import {
  useListFilters,
  useListPagination,
  useListResource,
  useCSVDownload,
} from 'api/list.hooks'
import { Box, Button, Tooltip } from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import DownloadIcon from '@mui/icons-material/Download'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { ConfirmWithDialogButton } from 'shared/ui'
import { Column } from 'shared/ui/Table/types'
import { TablePagination } from 'shared/ui/Table/TablePagination'
import { ResourceTypes } from 'providers/resources'
import { fetchResourceList, ListQueryParams } from 'api/listQueries'
import { useExportData, useIdNavigation } from './utility'

interface ResourceListProps<T> {
  title: string
  resourceKey: ResourceTypes
  columns: Column<T>[]
  header?: ReactNode
  onBulkDelete?: (ids: (number | string)[]) => Promise<unknown>
}

// PaginatedResourceList is used to display in a table grid a list of objects
// title is usually the resource type, for display only
// fetchFunc is the function used to retrieve a paginated list of the resource
// onRowClick usually used to navigate to the show page for the resource
// columns is the resource fields to display for each resource item
// onBulkDelete is an optional param, if added the user has the ability to trigger this action for all resource selected
export function PaginatedResourceList<T extends { id: number | string }>({
  title,
  resourceKey,
  header,
  columns,
  onBulkDelete,
}: ResourceListProps<T>) {
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const selectedReset = useCallback(() => setSelectedRows([]), [])
  const pagination = useListPagination()
  const idNavigate = useIdNavigation(resourceKey)

  const fetchFunc = useCallback(
    (params: ListQueryParams) => fetchResourceList<T>(resourceKey, params),
    [resourceKey],
  )
  const { data, reFetch } = useListResource(fetchFunc, selectedReset)
  const doExport = useExportData(resourceKey, data, columns)
  const csvExport = useCSVDownload(resourceKey)
  const { filters } = useListFilters()

  // disable csv export if no filters are set, no data is available, or if
  // the only filter is for roles
  const csvDisabled =
    Object.keys(filters).length === 0 ||
    !data.length ||
    (Object.keys(filters).length === 1 && !!filters.role)

  const doOnRowClick = useCallback(
    (index: number) => {
      idNavigate(data[index])
    },
    [idNavigate, data],
  )

  const onRowSelect = useCallback(
    (index: number) => {
      setSelectedRows((prev) => {
        // if index is -1 toggle select all/select none
        if (index === -1) {
          if (prev.length) {
            // if any are selected switch to select none
            return []
          }
          // if none are selected select all
          return Array.from(Array(data.length).keys())
        }
        // otherwise toggle the index selected
        if (prev.includes(index)) {
          return prev.filter((i) => i !== index)
        }
        return [...prev, index]
      })
    },
    [data],
  )

  const getSelectedIDs = () => {
    const ids = selectedRows.map((i) => data[i].id)
    return ids.toString()
  }

  const bulkActions = useMemo(() => {
    // if we don't have onBulkDelete we don't have any bulk actions
    if (!onBulkDelete) {
      return undefined
    }

    const onDelete = async () => {
      const ids = selectedRows.map((i) => data[i].id)
      selectedReset()
      await onBulkDelete(ids)
      return reFetch()
    }
    const hasActiveFilters = Object.keys(filters).length > 0
    const onDeleteAllFiltered = async () => {
      const result = await fetchResourceList<T>(resourceKey, {
        perPage: -1,
        currentPage: 1,
        filters,
        sort: { field: 'id', order: 'asc' },
      })
      const allIds = result.data.map((item) => item.id)
      selectedReset()
      await onBulkDelete(allIds)
      return reFetch()
    }

    const toolTips = [
      <Tooltip key="delete" title="Delete Selected">
        <ConfirmWithDialogButton
          dialogTitle={title}
          dialogContent={
            <Box>
              Delete the following IDs:
              <strong>{getSelectedIDs()}</strong>?
            </Box>
          }
          disabled={!selectedRows.length}
          onClick={onDelete}
        >
          <DeleteIcon color="warning" />
        </ConfirmWithDialogButton>
      </Tooltip>,
      <Tooltip key="delete-all" title="Delete All Filtered">
        <ConfirmWithDialogButton
          dialogTitle={`Delete All Filtered ${title}`}
          dialogContent={
            <Box>
              Delete <strong>all {pagination.total}</strong>{' '}
              {title.toLowerCase()} matching the current filters?
              {pagination.total > 500 && (
                <Box sx={{ mt: 1, color: 'warning.main' }}>
                  Note: Only the first 500 will be deleted. Refine your filters
                  to delete more.
                </Box>
              )}
            </Box>
          }
          disabled={!hasActiveFilters || pagination.total === 0}
          onClick={onDeleteAllFiltered}
        >
          <DeleteSweepIcon color="error" />
        </ConfirmWithDialogButton>
      </Tooltip>,
    ]
    // csvExport is the new csv export function that retrieves a zip file of the csv
    // from the backend
    if (csvExport) {
      toolTips.push(
        <Tooltip key="csv" title="CSV">
          <Button disabled={csvDisabled} onClick={csvExport}>
            <SaveAltIcon />
          </Button>
        </Tooltip>,
      )
    } else {
      // older version of export creates the csv of the current page result in the frontend
      toolTips.push(
        <Tooltip key="export" title="Export">
          <Button disabled={!data.length} onClick={doExport}>
            <DownloadIcon />
          </Button>
        </Tooltip>,
      )
    }
    return toolTips
  }, [
    selectedReset,
    selectedRows,
    onBulkDelete,
    doExport,
    data,
    reFetch,
    filters,
    pagination.total,
    resourceKey,
    csvDisabled,
    csvExport,
    getSelectedIDs,
    title,
  ])

  return (
    <Box>
      {header}
      <Table
        title={title}
        columns={columns}
        items={data}
        footer={<TablePagination {...pagination} />}
        onRowClick={doOnRowClick}
        selectedRows={selectedRows}
        onRowSelect={(bulkActions?.length && onRowSelect) || undefined}
        toolBarItems={bulkActions}
      />
    </Box>
  )
}
